if (!customElements.get('bundle-image-gallery')) {
  customElements.define(
    'bundle-image-gallery',
    class BundleImageGallery extends HTMLElement {
      connectedCallback() {
        this.featuredImage = this.querySelector('.bundle-gallery__featured-image');
        this.thumbsTrack = this.querySelector('.bundle-gallery__thumbs');
        this.prevButton = this.querySelector('.bundle-gallery__thumbs-nav--prev');
        this.nextButton = this.querySelector('.bundle-gallery__thumbs-nav--next');
        this._preloaded = new Map();
        this._swapToken = 0;

        if (this._isInitialized) return;
        this._isInitialized = true;

        this.preloadImages();
        this.addEventListener('click', this.onThumbClick.bind(this));
        this.initThumbNavigation();
      }

      preloadImages() {
        this.querySelectorAll('.bundle-gallery__thumb').forEach((thumb) => {
          const fullSrc = thumb.dataset.fullSrc;
          if (!fullSrc || this._preloaded.has(fullSrc)) return;

          const img = new Image();
          img.decoding = 'async';
          img.src = fullSrc;
          this._preloaded.set(fullSrc, img);
        });
      }

      initThumbNavigation() {
        if (!this.thumbsTrack || !this.prevButton || !this.nextButton) return;

        this.prevButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollThumbs(-1);
        });

        this.nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollThumbs(1);
        });

        this.thumbsTrack.addEventListener('scroll', () => this.updateThumbNavigation(), { passive: true });

        if (!this._resizeObserver && typeof ResizeObserver !== 'undefined') {
          this._resizeObserver = new ResizeObserver(() => this.updateThumbNavigation());
          this._resizeObserver.observe(this.thumbsTrack);
        }

        this.updateThumbNavigation();
      }

      getThumbScrollAmount() {
        const thumb = this.thumbsTrack?.querySelector('.bundle-gallery__thumb');
        if (!thumb) return 0;

        const trackStyles = window.getComputedStyle(this.thumbsTrack);
        const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;

        return thumb.offsetWidth + gap;
      }

      scrollThumbs(direction) {
        if (!this.thumbsTrack) return;

        const amount = this.getThumbScrollAmount();
        if (!amount) return;

        this.thumbsTrack.scrollBy({
          left: direction * amount,
          behavior: 'smooth',
        });
      }

      updateThumbNavigation() {
        if (!this.thumbsTrack || !this.prevButton || !this.nextButton) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.thumbsTrack;
        const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);
        const hasOverflow = maxScrollLeft > 1;

        this.prevButton.hidden = !hasOverflow;
        this.nextButton.hidden = !hasOverflow;

        if (!hasOverflow) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = true;
          return;
        }

        this.prevButton.disabled = scrollLeft <= 1;
        this.nextButton.disabled = scrollLeft >= maxScrollLeft - 1;
      }

      onThumbClick(event) {
        const thumb = event.target.closest('.bundle-gallery__thumb');
        if (!thumb || !this.contains(thumb)) return;
        event.preventDefault();
        this.setFeatured(thumb);
      }

      setFeatured(thumb) {
        if (!this.featuredImage || !thumb) return;

        const fullSrc = thumb.dataset.fullSrc;
        const thumbSrc = thumb.dataset.thumbSrc;
        const alt = thumb.dataset.alt || '';

        if (!fullSrc) return;

        const swapToken = ++this._swapToken;

        this.featuredImage.removeAttribute('srcset');
        this.featuredImage.sizes = '';
        this.featuredImage.alt = alt;

        if (thumbSrc) {
          this.featuredImage.src = thumbSrc;
        }

        const applyFullImage = () => {
          if (swapToken !== this._swapToken) return;
          this.featuredImage.src = fullSrc;
        };

        const preloaded = this._preloaded.get(fullSrc);
        if (preloaded?.complete) {
          applyFullImage();
        } else {
          const loader = preloaded || new Image();
          if (!preloaded) {
            loader.decoding = 'async';
            loader.src = fullSrc;
            this._preloaded.set(fullSrc, loader);
          }

          if (loader.complete) {
            applyFullImage();
          } else {
            loader.onload = applyFullImage;
          }
        }

        this.querySelectorAll('.bundle-gallery__thumb').forEach((button) => {
          const isActive = button === thumb;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.thumbsTrack && window.matchMedia('(max-width: 749px)').matches) {
          const trackRect = this.thumbsTrack.getBoundingClientRect();
          const thumbRect = thumb.getBoundingClientRect();

          if (thumbRect.left < trackRect.left || thumbRect.right > trackRect.right) {
            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          }
        }
      }
    }
  );

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('bundle-image-gallery').forEach((gallery) => {
      gallery.updateThumbNavigation?.();
    });
  });
}
